/**
 * Detalle de un conduce consignado — los motores que el consignante dejó en
 * esa entrega, con su estado actual y la devolución de los que sigan en piso.
 *
 * Mismo contrato que el detalle de venta x mayor (`facturadetallexmayor`):
 * recibe la fila del listado maestro, tiene su propia barra "Volver" y avisa
 * al maestro con `onChanged()` cuando algo cambia.
 *
 * Los contadores de la cabecera se calculan de las propias filas cargadas, no
 * del maestro: así no quedan desfasados tras devolver un motor.
 *
 * Si ya se vendió algún motor, el conduce se convirtió en una cuenta por
 * pagar y desde aquí se abre esa factura. Mientras no se venda ninguno la
 * factura no existe para tesorería, así que no hay a dónde enlazar.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import { puedeAccionPrograma, tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { FP, fpDataGridSx, fpInputSx, StatusPill } from "components/financepro";
import FacturaDetalleCxp from "modules/cuentasxpagar/facturadetallecxp";
import {
  imprimirDevolucionCxp,
  imprimirConduceDevolucion,
} from "modules/cuentasxpagar/impresionfacturacxp";

const parseN = (v) =>
  parseFloat(
    String(v ?? "0")
      .split(",")
      .join("")
  ) || 0;

const fmtMoney = (v) =>
  parseN(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function Dato({ label, value, strong = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "1.05rem" : "0.86rem",
          fontWeight: strong ? 800 : 600,
          color: FP.color.textPrimary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function esVendido(estado) {
  return (
    String(estado ?? "")
      .trim()
      .toUpperCase() === "V"
  );
}

export function esDevuelto(estado) {
  return (
    String(estado ?? "")
      .trim()
      .toUpperCase() === "D"
  );
}

// 'X': la línea existe en el conduce pero el motor no está en el inventario y
// nadie registró la devolución. No es deuda y no se puede devolver desde
// aquí, pero tampoco puede quedar invisible.
export function esHuerfano(estado) {
  return (
    String(estado ?? "")
      .trim()
      .toUpperCase() === "X"
  );
}

export function esDisponible(estado) {
  const s = String(estado ?? "")
    .trim()
    .toUpperCase();
  return s === "" || s === "A";
}

export function estadoPill(estado) {
  const s = String(estado ?? "")
    .trim()
    .toUpperCase();
  if (s === "V") return <StatusPill label="Vendido" variant="neutral" />;
  if (s === "D") return <StatusPill label="Devuelto" variant="danger" />;
  if (s === "X") return <StatusPill label="Sin inventario" variant="info" />;
  if (esDisponible(s)) return <StatusPill label="Disponible" variant="success" />;
  return <StatusPill label={estado || "—"} variant="info" />;
}

export default function ConduceDetalle({ consignacion, onExit, onChanged } = {}) {
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [busquedaChasis, setBusquedaChasis] = useState("");
  const [confirmRow, setConfirmRow] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [recibidopor, setRecibidopor] = useState("");
  // Devolución en lote: filas marcadas en la grilla y chasis pendientes de
  // confirmar (null = diálogo cerrado).
  const [seleccion, setSeleccion] = useState([]);
  const [loteChasis, setLoteChasis] = useState(null);
  // Pestañas: 0 = motores del conduce, 1 = devoluciones registradas.
  const [tab, setTab] = useState(0);
  const [devoluciones, setDevoluciones] = useState([]);
  const [cargandoDev, setCargandoDev] = useState(false);
  // Membrete (con logo) para los comprobantes impresos; lo devuelven los
  // endpoints de devoluciones y se conserva para las reimpresiones.
  const [compania, setCompania] = useState(null);

  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Devolver se habilita con consignaciones.borrar O ares8550.borrar (los
  // mismos dos que acepta el backend en /api/anularconsignacion).
  const puedeDevolver =
    puedeAccionPrograma(roleDetalles, "consignaciones", "borrar") ||
    puedeAccionPrograma(roleDetalles, "ares8550", "borrar");
  // El detalle de la factura va por ares8520; sin ese permiso el enlace solo
  // llevaría a un 403, así que ni se ofrece.
  const puedeVerCxp = tienePermisosModulo(roleDetalles, "ares8520");
  const [cxpAbierta, setCxpAbierta] = useState(false);

  // El motivo de una tabla vacía queda visible: un fallo de red o un 403 de
  // permisos solo mostraban una alerta que se auto-oculta, y la pantalla
  // quedaba indistinguible de "el conduce no tiene motores".
  async function cargarMotores() {
    if (!consignacion) return;
    setCargando(true);
    setError("");
    try {
      const { status, data } = await post("/api/consignacionesporfactura", {
        id: consignacion.id,
        nfactura: consignacion.nfactura,
        suplidor: consignacion.suplidor,
      });
      if (status === 200) {
        setRows(data.consignaciones || []);
      } else {
        const mensaje =
          data?.Error ||
          (status === 403
            ? "Su rol no tiene acceso a Consignaciones"
            : `No se pudieron cargar los motores del conduce (HTTP ${status})`);
        setRows([]);
        setError(mensaje);
      }
    } catch (err) {
      setRows([]);
      setError("No se pudo contactar el servidor: " + (err?.message || err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarMotores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignacion?.id]);

  // Devoluciones registradas de ESTA consignación, para la pestaña y su
  // reimpresión por grupo. Se recargan en cada entrada a la pestaña: pudieron
  // registrarse devoluciones desde la pestaña de motores.
  async function cargarDevoluciones() {
    if (!consignacion) return;
    setCargandoDev(true);
    try {
      const { status, data } = await post("/api/consignaciones_devoluciones", {
        id: consignacion.id,
        nfactura: consignacion.nfactura,
        suplidor: consignacion.suplidor,
      });
      setDevoluciones(status === 200 ? data.devoluciones || [] : []);
      if (status === 200 && data.compania) setCompania(data.compania);
    } catch (err) {
      setDevoluciones([]);
    } finally {
      setCargandoDev(false);
    }
  }

  useEffect(() => {
    if (tab === 1) cargarDevoluciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, consignacion?.id]);

  // Agrupadas por folio (lote); una devolución individual es su propio grupo.
  const gruposDevolucion = useMemo(() => {
    const grupos = new Map();
    devoluciones.forEach((d) => {
      const clave = d.lote != null ? `L${d.lote}` : `I${d.id}`;
      if (!grupos.has(clave)) grupos.set(clave, []);
      grupos.get(clave).push(d);
    });
    return Array.from(grupos.entries()).map(([clave, lista]) => {
      const reingresadas = lista.filter((d) => d.reingresada).length;
      let estado = "Parcial";
      if (reingresadas === 0) estado = "Vigente";
      else if (reingresadas === lista.length) estado = "Reingresada";
      return {
        id: clave,
        folio: lista[0].lote ?? lista[0].id,
        fecha: lista[0].fecha,
        cantidad: lista.length,
        valor: lista.reduce((s, d) => s + (parseFloat(d.valor) || 0), 0),
        motivo: lista[0].motivo,
        recibidopor: lista[0].recibidopor,
        usuario: lista[0].usuario,
        estado,
        lista,
      };
    });
  }, [devoluciones]);

  const reimprimirGrupo = (grupo) => {
    if (grupo.lista.length > 1) imprimirConduceDevolucion(grupo.lista, compania);
    else imprimirDevolucionCxp(grupo.lista[0], compania);
  };

  async function confirmarDevolucion() {
    if (!confirmRow) return;
    setAnulando(true);
    try {
      const { status, data } = await post("/api/anularconsignacion", {
        chasis: confirmRow.chasis,
        id: consignacion?.id,
        nfactura: confirmRow.nfactura,
        suplidor: confirmRow.suplidor ?? consignacion?.suplidor,
        motivo,
        recibidopor,
      });
      if (status === 200) {
        // La devolución ya no borra la línea: la marca. Se recarga para que
        // el motor pase a "Devuelto" en vez de desaparecer del conduce.
        // El comprobante se imprime igual que al devolver desde CxP.
        if (data?.compania) setCompania(data.compania);
        if (data?.devolucion) imprimirDevolucionCxp(data.devolucion, data.compania || compania);
        cargarMotores();
        if (onChanged) onChanged();
        setConfirmRow(null);
        setMotivo("");
        setRecibidopor("");
        showAlert({
          message: "Motor devuelto al consignante",
          color: "success",
          autoHideDuration: 3000,
        });
      } else {
        showAlert({
          message: data?.Error || "No se pudo devolver el motor",
          color: "error",
          autoHideDuration: 4000,
        });
      }
    } catch (err) {
      showAlert({
        message: "Error de red: " + err.message,
        color: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setAnulando(false);
    }
  }

  // Chasis en piso de las filas marcadas (la grilla puede tener marcada una
  // fila que dejó de estar disponible tras recargar).
  const chasisSeleccionados = useMemo(() => {
    const porId = new Map(rows.map((r) => [r.id != null ? String(r.id) : String(r.chasis), r]));
    return seleccion
      .map((id) => porId.get(String(id)))
      .filter((r) => r && esDisponible(r.estado))
      .map((r) => r.chasis);
  }, [seleccion, rows]);

  async function confirmarDevolucionLote() {
    if (!loteChasis || !loteChasis.length) return;
    setAnulando(true);
    try {
      const { status, data } = await post("/api/consignaciones_devolverlote", {
        id: consignacion?.id,
        nfactura: consignacion?.nfactura,
        suplidor: consignacion?.suplidor,
        chasislist: loteChasis,
        motivo,
        recibidopor,
      });
      if (status === 200) {
        if (data?.compania) setCompania(data.compania);
        if (data?.devoluciones?.length)
          imprimirConduceDevolucion(data.devoluciones, data.compania || compania);
        cargarMotores();
        if (onChanged) onChanged();
        setLoteChasis(null);
        setSeleccion([]);
        setMotivo("");
        setRecibidopor("");
        showAlert({
          message: data.ok || "Motores devueltos al consignante",
          color: "success",
          autoHideDuration: 3000,
        });
      } else {
        showAlert({
          message: data?.Error || "No se pudo devolver el lote",
          color: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (err) {
      showAlert({
        message: "Error de red: " + err.message,
        color: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setAnulando(false);
    }
  }

  const accionDevolver = (row) => {
    if (esDisponible(row.estado) && puedeDevolver) {
      return (
        <Tooltip title="Devolver el motor al consignante">
          <IconButton size="small" onClick={() => setConfirmRow(row)} sx={{ color: FP.color.danger }}>
            <AssignmentReturnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", fontWeight: 700 }}>
        —
      </Typography>
    );
  };

  const columns = useMemo(
    () => [
      { field: "fecha", headerName: "Fecha", minWidth: 100, flex: 0.6 },
      { field: "chasis", headerName: "Chasis", minWidth: 150, flex: 1 },
      { field: "marca", headerName: "Marca", minWidth: 110, flex: 0.7 },
      { field: "modelo", headerName: "Modelo", minWidth: 110, flex: 0.7 },
      { field: "colore", headerName: "Color", minWidth: 100, flex: 0.6 },
      { field: "sucursal", headerName: "Sucursal", minWidth: 120, flex: 0.7 },
      { field: "costo", headerName: "Costo", minWidth: 100, flex: 0.6 },
      { field: "precio", headerName: "Precio", minWidth: 100, flex: 0.6 },
      {
        field: "estado",
        headerName: "Estado",
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => estadoPill(params.value),
      },
      {
        field: "__devolver",
        headerName: "Acción",
        width: 90,
        minWidth: 90,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => accionDevolver(params.row),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puedeDevolver]
  );

  const enPiso = rows.filter((r) => esDisponible(r.estado));
  const vendidos = rows.filter((r) => esVendido(r.estado));
  const devueltos = rows.filter((r) => esDevuelto(r.estado));
  const sumaCosto = (lista) => lista.reduce((acc, r) => acc + parseN(r.costo), 0);

  const filasVista = useMemo(() => {
    const q = busquedaChasis.trim().toLowerCase();
    return rows.filter((r) => {
      let okEstado = true;
      if (filtroEstado === "disponibles") okEstado = esDisponible(r.estado);
      else if (filtroEstado === "vendidos") okEstado = esVendido(r.estado);
      else if (filtroEstado === "devueltos") okEstado = esDevuelto(r.estado);
      else if (filtroEstado === "sininventario") okEstado = esHuerfano(r.estado);
      const okChasis =
        !q ||
        String(r.chasis ?? "")
          .toLowerCase()
          .includes(q);
      return okEstado && okChasis;
    });
  }, [rows, filtroEstado, busquedaChasis]);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.4,
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onExit}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textPrimary }}
          >
            Volver
          </Button>
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1.05rem" }}>
            Conduce consignado #{consignacion?.nfactura}
          </Typography>
          {consignacion?.anulada ? <StatusPill label="Anulado" variant="danger" /> : null}
        </Box>
        {/* El conduce se vuelve cuenta por pagar en cuanto se vende un motor;
            hasta entonces la factura no existe para tesorería y no hay a
            dónde enlazar. */}
        {vendidos.length > 0 && puedeVerCxp && (
          <Button
            startIcon={<ReceiptLongOutlinedIcon />}
            onClick={() => setCxpAbierta(true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.8,
              color: FP.color.accentBlue,
              bgcolor: "#FFFFFF",
              border: `1px solid ${FP.color.accentBlue}`,
              "&:hover": { bgcolor: `${FP.color.accentBlue}12` },
            }}
          >
            Ver factura en Cuentas por Pagar
          </Button>
        )}
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ ...sectionBox, mb: 2.5 }}>
          <Grid container spacing={2.4}>
            <Grid item xs={12} sm={4} md={3}>
              <Dato label="CONSIGNANTE" value={consignacion?.suplidornombre || "—"} />
            </Grid>
            <Grid item xs={6} sm={2} md={1.5}>
              <Dato label="FECHA" value={consignacion?.fecha || "—"} />
            </Grid>
            <Grid item xs={6} sm={2} md={1.3}>
              <Dato label="MOTORES" value={String(rows.length)} />
            </Grid>
            <Grid item xs={4} sm={2} md={1.3}>
              <Dato label="EN PISO" value={String(enPiso.length)} />
            </Grid>
            <Grid item xs={4} sm={2} md={1.3}>
              <Dato label="VENDIDOS" value={String(vendidos.length)} />
            </Grid>
            <Grid item xs={4} sm={2} md={1.3}>
              <Dato label="DEVUELTOS" value={String(devueltos.length)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Dato label="COSTO EN PISO" value={fmtMoney(sumaCosto(enPiso))} strong />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Dato label="COSTO VENDIDO" value={fmtMoney(sumaCosto(vendidos))} strong />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Dato
                label="POR PAGAR"
                value={fmtMoney(consignacion?.porpagar ?? consignacion?.liquidable)}
                strong
              />
            </Grid>
          </Grid>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.8rem", mt: 1.6 }}>
            Los motores en piso siguen siendo del consignante y se le pueden devolver: no son deuda.
            Solo lo vendido se le paga, desde Cuentas por Pagar.
            {vendidos.length === 0 &&
              " Mientras no se venda ninguno, este conduce no aparece en Cuentas por Pagar."}
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  minHeight: 36,
                  fontSize: "0.9rem",
                },
              }}
            >
              <Tab label={`Motores del conduce (${rows.length})`} />
              <Tab label={`Devoluciones (${devueltos.length})`} />
            </Tabs>
            {tab === 0 && rows.length > 0 && (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                {puedeDevolver && enPiso.length > 0 && (
                  <>
                    <Button
                      size="small"
                      startIcon={<AssignmentReturnIcon />}
                      disabled={anulando || chasisSeleccionados.length === 0}
                      onClick={() => setLoteChasis(chasisSeleccionados)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 1.8,
                        color: "#FFFFFF",
                        bgcolor: FP.color.danger,
                        "&:hover": { bgcolor: FP.color.danger, filter: "brightness(0.92)" },
                        "&.Mui-disabled": { bgcolor: `${FP.color.danger}55`, color: "#FFFFFF" },
                      }}
                    >
                      Devolver seleccionados
                      {chasisSeleccionados.length ? ` (${chasisSeleccionados.length})` : ""}
                    </Button>
                    <Button
                      size="small"
                      disabled={anulando}
                      onClick={() => setLoteChasis(enPiso.map((r) => r.chasis))}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 1.8,
                        color: FP.color.danger,
                        bgcolor: "#FFFFFF",
                        border: `1px solid ${FP.color.danger}`,
                        "&:hover": { bgcolor: `${FP.color.danger}12` },
                      }}
                    >
                      Devolver todo lo en piso ({enPiso.length})
                    </Button>
                  </>
                )}
                <TextField
                  size="small"
                  placeholder="Buscar chasis"
                  value={busquedaChasis}
                  onChange={(e) => setBusquedaChasis(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...fpInputSx, minWidth: { xs: "100%", sm: 220 } }}
                />
                <FormControl size="small" sx={{ ...fpInputSx, minWidth: 170 }}>
                  <InputLabel id="filtro-estado-cons-label" shrink>
                    Estado
                  </InputLabel>
                  <Select
                    labelId="filtro-estado-cons-label"
                    label="Estado"
                    notched
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="disponibles">Disponibles</MenuItem>
                    <MenuItem value="vendidos">Vendidos</MenuItem>
                    <MenuItem value="devueltos">Devueltos</MenuItem>
                    <MenuItem value="sininventario">Sin inventario</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {tab === 1 ? (
              cargandoDev ? (
                <Box display="flex" alignItems="center" justifyContent="center" py={8} gap={1.5}>
                  <CircularProgress size={22} />
                  <Typography sx={{ color: FP.color.textSecondary }}>
                    Cargando devoluciones…
                  </Typography>
                </Box>
              ) : gruposDevolucion.length > 0 ? (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={gruposDevolucion}
                    columns={[
                      { field: "folio", headerName: "Folio", width: 90 },
                      { field: "fecha", headerName: "Fecha", width: 110 },
                      {
                        field: "cantidad",
                        headerName: "Motores",
                        width: 90,
                        align: "center",
                        headerAlign: "center",
                      },
                      {
                        field: "valor",
                        headerName: "Valor",
                        width: 120,
                        align: "right",
                        headerAlign: "right",
                        valueFormatter: (value) => fmtMoney(value),
                      },
                      { field: "motivo", headerName: "Motivo", minWidth: 160, flex: 1 },
                      { field: "recibidopor", headerName: "Recibido por", width: 140 },
                      { field: "usuario", headerName: "Registrada por", width: 130 },
                      {
                        field: "estado",
                        headerName: "Estado",
                        width: 120,
                        renderCell: (params) =>
                          params.value === "Vigente" ? (
                            <StatusPill label="Vigente" variant="danger" />
                          ) : (
                            <StatusPill label={params.value} variant="info" />
                          ),
                      },
                      {
                        field: "__imprimir",
                        headerName: "Reimprimir",
                        width: 100,
                        sortable: false,
                        disableColumnMenu: true,
                        align: "center",
                        headerAlign: "center",
                        renderCell: (params) => (
                          <Tooltip
                            title={
                              params.row.cantidad > 1
                                ? "Reimprimir el conduce de devolución del grupo"
                                : "Reimprimir el comprobante"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => reimprimirGrupo(params.row)}
                              sx={{ color: FP.color.navy }}
                            >
                              <PrintOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      },
                    ]}
                    getRowId={(row) => row.id}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: "100%" }, height: 460, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                >
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    Este conduce no tiene devoluciones registradas
                  </Typography>
                  <Typography
                    sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }}
                    textAlign="center"
                    px={2}
                  >
                    Las devoluciones hechas antes de que existiera este registro no aparecen aquí.
                  </Typography>
                </Box>
              )
            ) : cargando ? (
              <Box display="flex" alignItems="center" justifyContent="center" py={8} gap={1.5}>
                <CircularProgress size={22} />
                <Typography sx={{ color: FP.color.textSecondary }}>Cargando motores…</Typography>
              </Box>
            ) : error ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1.5}>
                <Typography sx={{ color: FP.color.danger, fontWeight: 700 }} textAlign="center">
                  {error}
                </Typography>
                <Button onClick={cargarMotores} sx={{ textTransform: "none", fontWeight: 700 }}>
                  Reintentar
                </Button>
              </Box>
            ) : filasVista.length > 0 ? (
              <>
                <Box
                  sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}
                >
                  <DataGrid
                    rows={filasVista}
                    columns={columns}
                    getRowId={(row) => (row.id != null ? String(row.id) : String(row.chasis))}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    checkboxSelection={puedeDevolver}
                    isRowSelectable={(params) => esDisponible(params.row.estado)}
                    rowSelectionModel={seleccion}
                    onRowSelectionModelChange={(modelo) => setSeleccion(modelo)}
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: "100%" }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box
                  sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}
                >
                  {filasVista.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `${row.chasis}-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: FP.color.textPrimary,
                              fontSize: "0.95rem",
                              lineHeight: 1.2,
                              wordBreak: "break-word",
                            }}
                          >
                            {row.chasis}
                          </Typography>
                          <Typography
                            sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", mt: 0.25 }}
                          >
                            {[row.marca, row.modelo, row.colore].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
                        >
                          {estadoPill(row.estado)}
                          {esDisponible(row.estado) && puedeDevolver && (
                            <IconButton
                              size="small"
                              onClick={() => setConfirmRow(row)}
                              sx={{ color: FP.color.danger }}
                            >
                              <AssignmentReturnIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                          gap: 1,
                        }}
                      >
                        {[
                          ["Sucursal", row.sucursal],
                          ["Costo", row.costo],
                          ["Precio", row.precio],
                          ["Fecha", row.fecha],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography
                              sx={{
                                color: FP.color.textSecondary,
                                fontSize: "0.66rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                color: FP.color.textPrimary,
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                              }}
                            >
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
              >
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  {rows.length === 0
                    ? "El conduce no tiene motores"
                    : "Ningún motor coincide con el filtro o la búsqueda"}
                </Typography>
                <Typography
                  sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }}
                  textAlign="center"
                  px={2}
                >
                  {rows.length > 0
                    ? "Cambie el estado o el texto de búsqueda del chasis."
                    : "Revise el conduce en el listado principal."}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* La factura de cuentas por pagar de este mismo conduce. Al volver
            se recargan los motores: desde allí se pudo tocar un chasis. */}
        <Dialog fullScreen open={cxpAbierta} onClose={() => setCxpAbierta(false)}>
          {cxpAbierta && (
            <FacturaDetalleCxp
              facturaId={consignacion?.id}
              onExit={() => {
                setCxpAbierta(false);
                cargarMotores();
              }}
              onChanged={() => {
                cargarMotores();
                if (onChanged) onChanged();
              }}
            />
          )}
        </Dialog>

        <Dialog
          open={!!confirmRow}
          onClose={() => (anulando ? null : setConfirmRow(null))}
          maxWidth="xs"
          fullWidth
          fullScreen={isXs}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="h6" fontWeight={800}>
              Devolver al consignante
            </Typography>
            <IconButton onClick={() => setConfirmRow(null)} disabled={anulando} sx={{ p: 1 }}>
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.92rem" }}>
              ¿Seguro que deseas devolver este motor
              {confirmRow ? ` (chasis ${confirmRow.chasis})` : ""}? Se eliminará del inventario, su
              línea quedará marcada como devuelta y se descontará su valor del total del conduce.
              Esta acción no se puede deshacer.
            </Typography>
            <TextField
              fullWidth
              label="Motivo de la devolución"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={anulando}
              sx={{ ...fpInputSx, mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Recibido por"
              value={recibidopor}
              onChange={(e) => setRecibidopor(e.target.value)}
              disabled={anulando}
              sx={{ ...fpInputSx, mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmRow(null)}
                disabled={anulando}
                sx={{
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  borderColor: FP.color.borderStrong,
                  "&:hover": { borderColor: FP.color.textSecondary, bgcolor: FP.color.surface },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<AssignmentReturnIcon />}
                onClick={confirmarDevolucion}
                disabled={anulando}
                sx={{
                  bgcolor: FP.color.danger,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.danger, filter: "brightness(0.92)" },
                }}
              >
                {anulando ? "Devolviendo…" : "Devolver"}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>

        {/* Devolución en lote: un solo motivo/recibido-por para todos los
            chasis y un conduce de devolución con folio al terminar. */}
        <Dialog
          open={!!loteChasis}
          onClose={() => (anulando ? null : setLoteChasis(null))}
          maxWidth="sm"
          fullWidth
          fullScreen={isXs}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="h6" fontWeight={800}>
              Devolver {loteChasis?.length ?? 0} motores al consignante
            </Typography>
            <IconButton onClick={() => setLoteChasis(null)} disabled={anulando} sx={{ p: 1 }}>
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.92rem" }}>
              Se devolverán en una sola operación (todo o nada), se eliminarán del inventario y se
              descontará su valor del conduce. Al terminar se imprime un conduce de devolución con
              folio para la firma del consignante. Esta acción no se puede deshacer.
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                p: 1.2,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.5,
                maxHeight: 140,
                overflowY: "auto",
                fontSize: "0.8rem",
                color: FP.color.textPrimary,
                fontFamily: "monospace",
              }}
            >
              {(loteChasis || []).join(" · ")}
            </Box>
            <TextField
              fullWidth
              label="Motivo de la devolución"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={anulando}
              sx={{ ...fpInputSx, mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Recibido por"
              value={recibidopor}
              onChange={(e) => setRecibidopor(e.target.value)}
              disabled={anulando}
              sx={{ ...fpInputSx, mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button
                variant="outlined"
                onClick={() => setLoteChasis(null)}
                disabled={anulando}
                sx={{
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  borderColor: FP.color.borderStrong,
                  "&:hover": { borderColor: FP.color.textSecondary, bgcolor: FP.color.surface },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<AssignmentReturnIcon />}
                onClick={confirmarDevolucionLote}
                disabled={anulando}
                sx={{
                  bgcolor: FP.color.danger,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.danger, filter: "brightness(0.92)" },
                }}
              >
                {anulando ? "Devolviendo…" : `Devolver ${loteChasis?.length ?? 0} motores`}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}
